import React, { useState } from 'react';
import { BarChart3, Building2, Factory, Shield, Zap } from 'lucide-react';
import PageMeta from '../components/PageMeta';
import ReportToolbar from '../components/ReportToolbar';
import SubscriptionReportSection from '../components/SubscriptionReportSection';

const sectors = [
  { id: 'auto', name: 'Auto', summary: 'Auto demand, margins, EV transitions and supply-chain resiliency.', coverage: 'Production • Demand • Premiumization', signal: 'Demand recovery', color: 'blue', icon: BarChart3 },
  { id: 'banking', name: 'Banking', summary: 'Credit growth, deposit mix, margin trends and loan quality.', coverage: 'Credit • NIM • Asset quality', signal: 'Stable spreads', color: 'indigo', icon: Building2 },
  { id: 'banking-august-2026', name: 'Banking August 2026', summary: 'Regulatory and cyclical developments in banking through the next cycle.', coverage: 'Policy • Liquidity • Credit', signal: 'Cyclical watch', color: 'amber', icon: Building2 },
  { id: 'banking-intelligence', name: 'Banking Intelligence', summary: 'Frameworks for underwriting, capital efficiency and sector risk.', coverage: 'Risk • Balance sheet • ROE', signal: 'Underwriting quality', color: 'purple', icon: Shield },
  { id: 'capital-goods', name: 'Capital Goods', summary: 'Capex, order books and execution trends across industrial firms.', coverage: 'Order book • Margin • Capex', signal: 'Order momentum', color: 'emerald', icon: Factory },
  { id: 'defence', name: 'Defence', summary: 'Indigenisation, order flow and policy-led shifts in OEMs and suppliers.', coverage: 'Policy • Contracts • Execution', signal: 'Execution watch', color: 'rose', icon: Shield },
  { id: 'defence-sector', name: 'Defence Sector', summary: 'A focused view on defence themes, procurement cycles and margin dynamics.', coverage: 'Procurement • Scale • Margin', signal: 'Defence cycle', color: 'amber', icon: Shield },
  { id: 'fmcg', name: 'FMCG', summary: 'Pricing power, category growth and consumer demand across core staples.', coverage: 'Volume • Pricing • Demand', signal: 'Demand elasticity', color: 'blue', icon: BarChart3 },
  { id: 'information-technology', name: 'Information Technology', summary: 'Digital transformation, AI monetisation and enterprise demand mix.', coverage: 'Revenue • Margin • AI', signal: 'Deal momentum', color: 'indigo', icon: Zap },
  { id: 'metals', name: 'Metals', summary: 'Commodity cycles, China demand and cost curves across value chains.', coverage: 'Price • Input cost • Utilization', signal: 'Cyclical breakouts', color: 'rose', icon: Factory },
  { id: 'nbfc-intelligence', name: 'NBFC Intelligence', summary: 'Funding models, stress trends and growth dynamics across NBFCs.', coverage: 'Funding • Credit • Asset quality', signal: 'Funding discipline', color: 'purple', icon: Building2 },
  { id: 'pharma', name: 'Pharma', summary: 'Export pipelines, regulatory updates and domestic growth drivers.', coverage: 'Earnings • Regulation • Launches', signal: 'Margin expansion', color: 'emerald', icon: BarChart3 },
  { id: 'power', name: 'Power', summary: 'Generation mix, transmission investments and fuel-sensitivity across power firms.', coverage: 'Demand • Utilization • Capex', signal: 'Grid expansion', color: 'amber', icon: Zap }
];

const SectorIntelligence = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSector, setSelectedSector] = useState('All sectors');
  const [reportCount, setReportCount] = useState(0);
  const [viewMode, setViewMode] = useState('grid');

  return (
    <>
      <PageMeta
        title="Sector Intelligence | The B Tree"
        description="Understand sector structure, demand cycles, competitive dynamics, capital allocation, and long-term industry themes with B Tree Sector Intelligence."
        canonical="https://www.thebtree.com/sector-intelligence"
        image="https://www.thebtree.com/logo.png"
        type="website"
      />
    <div className="min-h-screen bg-[#F7F5F2]" data-testid="sector-intelligence-page">
      <section className="bg-gradient-to-b from-[#FDFCFB] to-[#F7F5F2] border-b border-[#C8A84B]/20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12 md:py-16">
          <p className="text-[11px] font-mono tracking-[0.3em] uppercase text-[#C8A84B] font-semibold mb-3">The B Tree Intelligence</p>
          <h1 className="text-4xl md:text-6xl font-[Cormorant_Garamond] font-medium text-[#0D1B2A] leading-tight mb-4">
            Sector Intelligence
          </h1>
          <p className="max-w-2xl text-lg text-slate-600 leading-relaxed">
            Sector-level research for understanding the forces behind earnings, valuations and capital flows.
          </p>
        </div>
        <ReportToolbar
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search sectors, themes or coverage..."
          reportCount={reportCount}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          testId="sector-intelligence"
          filterContent={(
            <select value={selectedSector} onChange={(event) => setSelectedSector(event.target.value)} className="h-10 w-64 max-w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-700 cursor-pointer">
              <option>All sectors</option>
              {sectors.map((sector) => <option key={sector.id}>{sector.name}</option>)}
            </select>
          )}
        />
      </section>

      <main className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12">
        <SubscriptionReportSection
          category="Sector"
          title="Uploaded Sector Intelligence™ Reports"
          onCountChange={setReportCount}
          viewMode={viewMode}
          filters={{
            query: searchQuery,
            primary: selectedSector,
            primaryAllValues: ['All sectors', 'All Sectors', 'All'],
          }}
        />

      </main>
    </div>
    </>
  );
};

export default SectorIntelligence;
